import React from 'react'
import { Formik } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { Left as LeftIcon } from '@urbaninfrastructure/react-icons'
import {
  Container,
  Flex,
  Box,
  Alert,
  Hide,
  Anchor,
  Button,
  Link,
} from '../../components'
import Layout from '../../components/Layout'
import AdministratorStep from './Step/AdministratorStep'
import CompanyStep from './Step/CompanyStep'
import EmailStep from './Step/EmailStep'
import IntroStep from './Step/IntroStep'
import OfficeStep from './Step/OfficeStep'
import SummaryStep from './Step/SummaryStep'
import messages from './messages'
import { REGISTER_COMPANY_MUTATION } from './graphql'
import { useSystemConfig } from '../../lib/use-system-config'
import {
  registerCompanyMutation,
  registerCompanyMutationVariables,
} from '../../core-types'
import { useMutation } from '@apollo/react-hooks'
import { useTenantAuth } from '../../lib/firebase'
import { checkForError } from '../../lib/util'
import { Values } from './types'
import { useRouter } from 'next/router'
import { useToasts } from '@urbaninfrastructure/toasts'

interface State {
  step: number
  submitCount: number
}

type Action =
  | { type: 'GO_NEXT' }
  | { type: 'GO_PREV' }
  | { type: 'FORM_SUBMIT' }

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'GO_NEXT': {
      return { ...state, step: state.step + 1, submitCount: 0 }
    }
    case 'GO_PREV': {
      return { ...state, step: state.step - 1, submitCount: 0 }
    }
    case 'FORM_SUBMIT': {
      return { ...state, submitCount: state.submitCount + 1 }
    }
    default: {
      return state
    }
  }
}

function ProgressIndicator({ numberOfSteps, ...props }) {
  const currentStepIndex = props.currentStepIndex - 1
  const arr = Array.from(Array(numberOfSteps).keys())
  return (
    <Flex justifyContent="center" py={2} {...props}>
      {arr.map((i) => {
        const isPassed = currentStepIndex >= i
        return (
          <Box
            key={i}
            size="8px"
            bg={isPassed ? 'primary' : 'neutral.4'}
            borderRadius={1000}
            m={1}
          >
            {currentStepIndex === i && (
              <Hide srOnly all>
                Step {currentStepIndex + 1} of {numberOfSteps}
              </Hide>
            )}
          </Box>
        )
      })}
    </Flex>
  )
}

const Registration = () => {
  const intl = useIntl()
  const router = useRouter()
  const systemConfig = useSystemConfig()
  const toasts = useToasts()
  const tenantAuth = useTenantAuth(null)
  const [registerCompany, { error, loading, client }] = useMutation<
    registerCompanyMutation,
    registerCompanyMutationVariables
  >(REGISTER_COMPANY_MUTATION, {
    onCompleted: (data) => {
      tenantAuth
        .signInWithCustomToken(data.registerCompany.token)
        .then(() => {
          client &&
            client.resetStore().then(() => {
              router.push('/company/payment')
            })
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err)
          toasts.addToast({
            text:
              'Account was created, but we could not sign you in. Try signing in again.',
            type: 'error',
          })
          router.push('/')
        })
    },
    onError: () => {
      // error handled in UI
    },
  })
  const [state, dispatch] = React.useReducer(reducer, {
    step: 0,
    submitCount: 0,
  })

  const title = intl.formatMessage(messages.title)

  const numberOfSteps = 5
  const isLastStep = state.step === numberOfSteps

  const administratorAlreadyRegisteredError = checkForError(
    'ADMINISTRATOR_ALREADY_REGISTERED',
    error
  )

  return (
    <Layout
      title={title}
      loading={false}
      hideChrome={!!state.step}
      isPublic
      contentProps={{
        mx: 0,
        mb: 0,
        py: 0,
      }}
      render={() => {
        return (
          <Formik<Values>
            onSubmit={(values, { setTouched, setFieldValue }) => {
              if (isLastStep) {
                registerCompany({
                  variables: {
                    company: {
                      name: values.companyName,
                      organizationNumber: values.companyOrgNr,
                      address: values.address,
                      zipCode: values.zipCode,
                      city: values.city,
                      receiptEmail: values.receiptEmail,
                    },
                    administrator: {
                      name: values.administratorName,
                      email: values.administratorEmail,
                      password: values.administratorPassword,
                    },
                  },
                })
              } else {
                if (
                  values.administratorEmail !== '' &&
                  values.receiptEmail === ''
                ) {
                  setFieldValue('receiptEmail', values.administratorEmail)
                }

                dispatch({ type: 'GO_NEXT' })
                setTouched({})
              }
            }}
            initialValues={{
              companyName: '',
              companyOrgNr: '',
              address: '',
              zipCode: '',
              city: '',
              receiptEmail: '',
              administratorName: '',
              administratorEmail: '',
              administratorPassword: '',
            }}
          >
            {({ handleSubmit }) => {
              function onFormSubmit(event) {
                dispatch({ type: 'FORM_SUBMIT' })
                handleSubmit(event)
              }

              if (state.step === 0) {
                return (
                  <form onSubmit={onFormSubmit} noValidate>
                    <Container pt={[0, 0, 0, 7]}>
                      <IntroStep />
                    </Container>
                  </form>
                )
              }

              return (
                <form onSubmit={onFormSubmit} noValidate>
                  <Box my="xs" position="relative">
                    <Anchor
                      onClick={() => dispatch({ type: 'GO_PREV' })}
                      textDecoration="none"
                      color="primary"
                      p={0}
                      m={0}
                      typoStyle="xxs"
                      // @ts-ignore
                      css={`
                        position: absolute;
                        left: 1rem;
                        top: 0;
                        line-height: 1;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                      `}
                    >
                      <LeftIcon size="32px" color="primary" />

                      <Hide xs sm srOnly>
                        <FormattedMessage {...messages.back} />
                      </Hide>
                    </Anchor>

                    <ProgressIndicator
                      currentStepIndex={state.step}
                      numberOfSteps={numberOfSteps}
                    />
                  </Box>

                  <Flex
                    alignItems="stretch"
                    justifyContent="center"
                    flexDirection="column"
                    minHeight="80vh"
                    position="relative"
                    overflow="hidden"
                  >
                    <Container>
                      {error && (
                        <Box mb="sm" mx="auto" maxWidth="max-content">
                          {administratorAlreadyRegisteredError && (
                            <Alert variant="error">
                              <FormattedMessage
                                id="administrator_already_exists"
                                defaultMessage="An administrator with this e-mail already exists. You can try <link>signing in</link>."
                                values={{
                                  // eslint-disable-next-line react/display-name
                                  link: (...chunks) => (
                                    <Link href="/">
                                      <Anchor>{chunks}</Anchor>
                                    </Link>
                                  ),
                                }}
                              />
                            </Alert>
                          )}
                          {!administratorAlreadyRegisteredError && (
                            <Alert variant="error">
                              <FormattedMessage {...messages.registerError} />
                            </Alert>
                          )}
                        </Box>
                      )}
                      <Box
                        width={
                          state.step !== 5
                            ? {
                                md: 2 / 3,
                                lg: 1 / 2,
                                xl: 1 / 3,
                              }
                            : null
                        }
                        mx="auto"
                      >
                        {state.step === 1 && (
                          <CompanyStep
                            enableBRREGLookup={
                              systemConfig.featureFlags.enableBRREGLookup ||
                              false
                            }
                          />
                        )}

                        {state.step === 2 && <OfficeStep />}

                        {state.step === 3 && <AdministratorStep />}

                        {state.step === 4 && <EmailStep />}

                        {state.step === 5 && (
                          <SummaryStep
                            loading={loading}
                            error={error}
                            intl={intl}
                          />
                        )}

                        {!isLastStep && (
                          <Box textAlign="center">
                            <Button
                              type="submit"
                              shape="cool"
                              variant="primary"
                            >
                              <FormattedMessage {...messages.next} />
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Container>
                  </Flex>
                </form>
              )
            }}
          </Formik>
        )
      }}
    />
  )
}

export default Registration
